import type { MutableRefObject, ReactNode, TransitionEvent } from "react"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import {
	IconArrowUpFill18 as ArrowUp,
	IconPaperPlane4Fill18 as PaperPlane,
} from "nucleo-ui-fill-18"
import {
	IconMicrophoneOutline18 as Microphone,
	IconPaperclip2Outline18 as Paperclip,
} from "nucleo-ui-outline-18"

import {
	cn,
	getClipboardFiles,
	pasteIsFilesOnly,
} from "@productlane/lib/src/browser"

import type { Attachment } from "../hooks/useAttachments"
import type { ChatInputSubmitIcon } from "./chat-input-icons"
import type { ChatInputTextPaste } from "./chat-input-text-paste"
import type { TranscribeAudio } from "./use-audio-input"
import type { SpeechInputError } from "./use-speech-input"
import { FileAttachments } from "./attachments/file-attachments"
import { FileUploadInput } from "./attachments/file-upload-input"
import { Button } from "./button"
import {
	CHAT_INPUT_ATTACH_STROKE_WIDTH,
	CHAT_INPUT_IDLE_ICON_COLOR,
	chatInputAttachIconClasses,
	chatInputStopIconClasses,
	chatInputSubmitIconClasses,
} from "./chat-input-icons"
import {
	CHAT_INPUT_ATTACHMENTS_CLASSES,
	CHAT_INPUT_TEXT_INSET_CLASS,
	chatInputPaddingClasses,
} from "./chat-input-padding"
import { isLongPaste } from "./chat-input-text-paste"
import { ChatInputTextPastes } from "./chat-input-text-pastes"
import { Textarea } from "./form/textarea"
import { useAudioInput } from "./use-audio-input"
import { joinDictation, useSpeechInput } from "./use-speech-input"
import { VoiceInputButton } from "./voice-input-button"
import { VoiceRecordingBar } from "./voice-recording-bar"

interface Props {
	value?: string
	setValue: (value: string) => void
	onSubmit: () => void
	// Called instead of onSubmit when the send button cannot fire, so a caller
	// can say why rather than leaving the press unanswered.
	onBlockedSubmit?: () => void
	disabled?: boolean
	// Locks the text field itself, for a composer the caller will refuse to
	// send from at all. `disabled` alone only stops the send.
	inputDisabled?: boolean
	placeholder?: string
	onInput?: () => void
	attachments?: Attachment[]
	onFilesAdded?: (files: File[]) => Promise<void>
	onRemoveAttachment?: (id: string) => Promise<void>
	inputId?: string
	accept?: string
	attachmentsDisabled?: boolean
	className?: string
	containerClassName?: string
	error?: string | null
	onError?: (message: string) => void
	autoFocus?: boolean
	// Off for inputs that remount on navigation, where replaying the entrance
	// animation reads as a glitch.
	animateOnMount?: boolean
	// Ease the box to its new height when lines or attachments change it.
	animateHeight?: boolean
	// Handle on the textarea so a caller can focus it on its own trigger.
	inputRef?: MutableRefObject<HTMLTextAreaElement | null>
	alwaysExpanded?: boolean
	// When generating, the send button becomes a stop button that calls onStop.
	isGenerating?: boolean
	onStop?: () => void
	submitIcon?: ChatInputSubmitIcon
	// Replaces the outlined look of the send button while there is nothing to send.
	idleSubmitClassName?: string
	// Controls that live inside the box, on a row under the textarea, left of
	// the send button. Pass undefined (not a component that renders null) when
	// there is nothing to show, or the row still claims its space.
	leadingActions?: ReactNode
	// A paste too long for the composer becomes a pill the caller owns and
	// folds back into the message on submit.
	textPastes?: ChatInputTextPaste[]
	onTextPaste?: (text: string) => void
	onRemoveTextPaste?: (id: string) => void
	// Replaces the textarea with a caller-owned input, so a surface that needs
	// a rich composer keeps the same chrome. `value` still drives send state.
	inputSlot?: ReactNode
	onRequestFocus?: () => void
	// Sizes every control to 32px, so a one-line composer is as
	// short as its controls. For the spotlight bar, which is only ever one line.
	compact?: boolean
	// Dictation. Off by default; a composer opts in by saying where a spoken
	// phrase goes. A rich `inputSlot` owns the insert, a plain textarea gets
	// `appendToValue`, which folds the phrase into `value`.
	voiceInput?: "appendToValue" | ((text: string) => void) | false
	onVoiceError?: (error: SpeechInputError) => void
	transcribeAudio?: TranscribeAudio
	// Starts recording once the composer can, then reports back so it fires once.
	autoStartVoice?: boolean
	onAutoStartVoice?: () => void
	// Hides the mic while dictation stays wired, so a recording in flight still
	// lands. The spotlight bar shows it only once the bar opens.
	showVoiceButton?: boolean
	// Off, the send button and its neighbours wait invisible in their slots and
	// slide in from the right when it turns on. The spotlight bar rests this way.
	actionsVisible?: boolean
}

// Rightmost first, so the controls arrive as one sweep from the edge.
const REVEAL_DELAY_CLASSES = [
	"",
	"[transition-delay:45ms]",
	"[transition-delay:90ms]",
] as const
const REVEAL_TRANSITION =
	"[transition-property:opacity,transform,filter,background-color] duration-[280ms] [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] motion-reduce:translate-x-0 motion-reduce:blur-0"
// Important so a disabled send button's own disabled:opacity-100 cannot show it.
const REVEAL_HIDDEN =
	"pointer-events-none translate-x-2.5 !opacity-0 blur-[3px]"

const buildAcceptMatcher = (accept?: string) => {
	if (!accept) return () => true
	const tokens = accept
		.split(",")
		.map((t) => t.trim())
		.filter(Boolean)
	const exact = new Set(
		tokens.filter((t) => !t.includes("/*") && !t.startsWith(".")),
	)
	const wildcards = tokens.filter((t) => t.endsWith("/*"))
	const extensions = new Set(
		tokens.filter((t) => t.startsWith(".")).map((t) => t.toLowerCase()),
	)
	return (file: File) => {
		if (exact.has(file.type)) return true
		if (wildcards.some((w) => file.type.startsWith(w.slice(0, -1)))) return true
		const ext = "." + file.name.split(".").pop()?.toLowerCase()
		return extensions.has(ext)
	}
}

/** Long enough to read twice, short enough to stay a toast. */
const VOICE_ERROR_MS = 6000

export const ChatInput = (props: Props) => {
	const {
		value,
		onInput,
		setValue,
		onSubmit,
		onBlockedSubmit,
		disabled = false,
		inputDisabled = false,
		placeholder = "",
		attachments = [],
		onFilesAdded,
		onRemoveAttachment,
		inputId = "chat-input",
		accept,
		attachmentsDisabled = false,
		className,
		containerClassName,
		error,
		onError,
		autoFocus = true,
		animateOnMount = true,
		animateHeight = false,
		inputRef,
		alwaysExpanded = false,
		isGenerating = false,
		onStop,
		submitIcon = "paper-plane",
		idleSubmitClassName,
		leadingActions,
		textPastes = [],
		onTextPaste,
		onRemoveTextPaste,
		inputSlot,
		onRequestFocus,
		compact = false,
		voiceInput = false,
		onVoiceError,
		transcribeAudio,
		autoStartVoice = false,
		onAutoStartVoice,
		showVoiceButton = true,
		actionsVisible = true,
	} = props

	const [voiceError, setVoiceError] = useState<string | null>(null)
	const displayedError = error ?? voiceError

	useEffect(() => {
		if (!voiceError) return
		const timer = setTimeout(() => setVoiceError(null), VOICE_ERROR_MS)
		return () => clearTimeout(timer)
	}, [voiceError])
	const handleVoiceError = (reason: SpeechInputError) => {
		if (onVoiceError) {
			onVoiceError(reason)
			return
		}
		setVoiceError(
			reason === "denied"
				? "Microphone access was blocked. Check your browser permissions or contact the site owner."
				: reason === "insecure-origin"
					? "Voice input requires a secure HTTPS connection."
					: "Voice input failed. Please try again.",
		)
	}

	const handleTranscript =
		voiceInput === false
			? undefined
			: voiceInput === "appendToValue"
				? (text: string) => setValue(joinDictation(value ?? "", text))
				: voiceInput

	const speech = useSpeechInput({
		onTranscript: (text) => {
			setVoiceError(null)
			handleTranscript?.(text)
		},
		onError: handleVoiceError,
	})
	const cancelDictation = speech.cancel
	// An inputSlot sends on its own Enter, so an emptied composer is the send signal.
	useEffect(() => {
		if (!value) cancelDictation()
	}, [value, cancelDictation])

	const audio = useAudioInput({
		transcribe: transcribeAudio,
		onTranscript: (text) => {
			setVoiceError(null)
			handleTranscript?.(text)
		},
		onError: setVoiceError,
	})
	const recordingActive = audio.state !== "idle"
	const canRecord = !!handleTranscript && !!transcribeAudio && audio.supported
	// Browsers only expose the microphone on https or localhost; keep the mic so a click can say so.
	const recordingNeedsSecureOrigin =
		!!handleTranscript &&
		!!transcribeAudio &&
		!audio.supported &&
		typeof window !== "undefined" &&
		!window.isSecureContext
	const showRecordButton =
		showVoiceButton && (canRecord || recordingNeedsSecureOrigin)
	// Controls that arrive after mount (the spotlight bar opening) slide in from
	// the right; elsewhere they are there from the first paint.
	const lateControlClasses =
		"[@starting-style]:translate-x-2.5 [@starting-style]:opacity-0 [@starting-style]:blur-[3px]"
	const revealClasses = (order: 0 | 1 | 2) =>
		cn(
			REVEAL_TRANSITION,
			actionsVisible ? REVEAL_DELAY_CLASSES[order] : REVEAL_HIDDEN,
		)
	useEffect(() => {
		if (!autoStartVoice || disabled) return
		onAutoStartVoice?.()
		if (recordingNeedsSecureOrigin) {
			handleVoiceError("insecure-origin")
			return
		}
		if (!canRecord) {
			setVoiceError(
				"This browser can't record audio. Type your question instead.",
			)
			return
		}
		setVoiceError(null)
		void audio.start()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoStartVoice, canRecord, disabled])
	const SubmitIcon = submitIcon === "arrow-up" ? ArrowUp : PaperPlane

	const hasText = Boolean(value && value.trim().length > 0)
	const isUploading = attachments.some((a) => a.state === "loading")
	const hasReadyAttachment = attachments.some((a) => a.state !== "loading")
	const sendEnabled =
		(hasText || hasReadyAttachment || textPastes.length > 0) &&
		!isUploading &&
		!disabled
	const handleSubmitAttempt = () => {
		if (sendEnabled) {
			cancelDictation()
			onSubmit()
			return
		}
		onBlockedSubmit?.()
	}

	const textareaRef = useRef<HTMLTextAreaElement | null>(null)
	const wasRecording = useRef(false)
	useEffect(() => {
		if (wasRecording.current && !recordingActive) {
			if (inputSlot) onRequestFocus?.()
			else textareaRef.current?.focus()
		}
		wasRecording.current = recordingActive
	}, [recordingActive, inputSlot, onRequestFocus])
	const inputWrapperRef = useRef<HTMLDivElement | null>(null)
	const boxRef = useRef<HTMLDivElement | null>(null)
	const contentRef = useRef<HTMLDivElement | null>(null)
	const [boxHeight, setBoxHeight] = useState<number | null>(null)
	// Clip only while the height moves, so an attachment's remove button can
	// sit over the box edge the rest of the time.
	const [isResizing, setIsResizing] = useState(false)
	const reduceMotion = useReducedMotion()

	useLayoutEffect(() => {
		const box = boxRef.current
		const content = contentRef.current
		if (!animateHeight || !box || !content) return
		if (typeof ResizeObserver === "undefined") return
		let previous: number | null = null
		const observer = new ResizeObserver(() => {
			const style = getComputedStyle(box)
			const next =
				content.offsetHeight +
				parseFloat(style.borderTopWidth) +
				parseFloat(style.borderBottomWidth)
			if (previous !== null && next !== previous && !reduceMotion) {
				setIsResizing(true)
			}
			previous = next
			setBoxHeight(next)
		})
		observer.observe(content)
		return () => observer.disconnect()
	}, [animateHeight, reduceMotion])

	const endResize = (e: TransitionEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget && e.propertyName === "height") {
			setIsResizing(false)
		}
	}
	const [isDragging, setIsDragging] = useState(false)
	const [attachOnMount] = useState(() => onFilesAdded !== undefined)
	const [voiceButtonOnMount] = useState(() => showVoiceButton)
	const [isFocused, setIsFocused] = useState(false)
	// Once the textarea wraps past a single line, lock into the expanded
	// (column) layout. We reset back to the inline (row) layout when the
	// input empties to avoid oscillation between the two widths.
	const [isMultiLine, setIsMultiLine] = useState(alwaysExpanded)
	// Keep overflow-hidden until at maxRows; otherwise macOS elastic-bounces the textarea even when content fits.
	const [isAtMaxRows, setIsAtMaxRows] = useState(false)

	useEffect(() => {
		if (alwaysExpanded) {
			setIsMultiLine(true)
			return
		}
		if (!value || value.length === 0) setIsMultiLine(false)
	}, [value, alwaysExpanded])

	const isAcceptedFile = useMemo(() => buildAcceptMatcher(accept), [accept])

	const handleDroppedOrPastedFiles = (files: File[]) => {
		if (!onFilesAdded || files.length === 0) return
		const valid = files.filter(isAcceptedFile)
		const invalid = files.filter((f) => !isAcceptedFile(f))
		if (valid.length > 0) {
			void onFilesAdded(valid)
		}
		if (invalid.length > 0 && onError) {
			onError(`Unsupported file type: ${invalid.map((f) => f.name).join(", ")}`)
		}
	}

	return (
		<div
			className="relative w-full"
			// A one-line composer keeps its own height, so the bar swaps in place.
			style={recordingActive && !compact ? { minHeight: 56 } : undefined}
		>
			{/* Floated, not stacked: an error that pushes the composer down makes
			    the bar jump under the cursor mid-sentence. */}
			<AnimatePresence>
				{displayedError && (
					<motion.p
						role="status"
						initial={{ opacity: 0, transform: "translateY(4px)" }}
						animate={{ opacity: 1, transform: "translateY(0px)" }}
						exit={{ opacity: 0, transform: "translateY(4px)" }}
						transition={{ duration: 0.15 }}
						className="pointer-events-none absolute bottom-full left-0 right-0 z-20 mx-auto mb-2 w-fit max-w-full rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10"
					>
						{displayedError}
					</motion.p>
				)}
			</AnimatePresence>
			<motion.div
				ref={boxRef}
				inert={recordingActive || undefined}
				style={{
					...(recordingActive ? { visibility: "hidden" } : undefined),
					// The content sits at the bottom of the box, so while the box catches
					// up it reveals upward and the border and buttons never clip.
					...(animateHeight && boxHeight !== null
						? {
								height: boxHeight,
								transition: reduceMotion
									? undefined
									: "box-shadow 150ms, border-color 150ms, height 200ms cubic-bezier(0.23, 1, 0.32, 1)",
							}
						: undefined),
				}}
				initial={animateOnMount ? { opacity: 0, y: 20 } : false}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.15, delay: 0.2 }}
				className={cn(
					"w-full cursor-text rounded-lg bg-gray-50 px-2 shadow-sm ring-1 ring-black/10 transition-[box-shadow,border-color] duration-150 dark:bg-gray-800 dark:ring-white/10",
					animateHeight && "flex flex-col justify-end",
					isResizing && "overflow-hidden",
					isDragging && "ring-accent dark:ring-accent-dark ring-2",
					containerClassName,
					isFocused &&
						!isDragging &&
						"border-black/25 ring-black/25 dark:border-white/25 dark:ring-white/25",
				)}
				onClick={(e) => {
					// A click already inside the field placed the caret; refocusing
					// would move it to the end.
					if (
						e.target instanceof Node &&
						inputWrapperRef.current?.contains(e.target)
					) {
						return
					}
					if (inputSlot) {
						onRequestFocus?.()
						return
					}
					textareaRef.current?.focus()
				}}
				onDragOver={
					onFilesAdded
						? (e) => {
								e.preventDefault()
								setIsDragging(true)
							}
						: undefined
				}
				onTransitionEnd={endResize}
				onTransitionCancel={endResize}
				onDragLeave={onFilesAdded ? () => setIsDragging(false) : undefined}
				onDrop={
					onFilesAdded
						? (e) => {
								e.preventDefault()
								setIsDragging(false)
								handleDroppedOrPastedFiles(Array.from(e.dataTransfer.files))
							}
						: undefined
				}
			>
				<div
					ref={contentRef}
					className={cn(
						"shrink-0",
						chatInputPaddingClasses({
							isMultiLine,
							hasAttachments: attachments.length > 0 || textPastes.length > 0,
						}),
					)}
				>
					{onFilesAdded && (
						<FileUploadInput
							id={inputId}
							accept={accept}
							onFilesAdded={async (newFiles) => {
								await onFilesAdded(newFiles.map((file) => file.file))
							}}
						/>
					)}
					{onRemoveTextPaste && (
						<ChatInputTextPastes
							pastes={textPastes}
							onRemove={onRemoveTextPaste}
							className={CHAT_INPUT_ATTACHMENTS_CLASSES}
						/>
					)}
					{attachments.length > 0 && (
						<FileAttachments
							attachments={attachments}
							className={CHAT_INPUT_ATTACHMENTS_CLASSES}
							showNameLabel
							enableViewer
							onRemove={async (id) => {
								if (onRemoveAttachment) {
									await onRemoveAttachment(id)
								}
							}}
						/>
					)}
					<div
						className={cn(
							"flex w-full",
							isMultiLine || leadingActions
								? "flex-col gap-2"
								: "flex-row items-center gap-1",
						)}
					>
						<div ref={inputWrapperRef} className="relative min-w-0 flex-grow">
							{inputSlot ?? (
								<Textarea
									value={value}
									disabled={inputDisabled}
									autoFocus={autoFocus}
									ref={(el) => {
										textareaRef.current = el
										if (inputRef) inputRef.current = el
									}}
									onFocus={() => setIsFocused(true)}
									onBlur={() => setIsFocused(false)}
									onInput={onInput}
									onChange={(e) => setValue(e.target.value)}
									className={cn(
										"flex-grow font-medium tracking-tight text-primary outline-none",
										CHAT_INPUT_TEXT_INSET_CLASS,
										className,
									)}
									inputClassName={isAtMaxRows ? undefined : "overflow-hidden"}
									placeholder={placeholder}
									autoComplete="off"
									data-1p-ignore="true"
									data-lpignore="true"
									data-protonpass-ignore="true"
									data-bwignore="true"
									data-form-type="other"
									autoCorrect="false"
									variant="borderless"
									textSize="md"
									name="message"
									onPaste={(e) => {
										const pastedText =
											e.clipboardData?.getData("text/plain") ?? ""
										if (onTextPaste && isLongPaste(pastedText)) {
											e.preventDefault()
											onTextPaste(pastedText)
											return
										}
										if (!onFilesAdded) return
										const files = getClipboardFiles(e.clipboardData?.items)
										if (files.length === 0) return
										// Text on the clipboard is the paste; a file that travelled
										// with it must not swallow it.
										const hasText = Boolean(
											e.clipboardData?.getData("text/plain"),
										)
										if (pasteIsFilesOnly(files, hasText)) e.preventDefault()
										handleDroppedOrPastedFiles(files)
									}}
									onKeyDown={(e) => {
										if (
											e.key === "Enter" &&
											!e.shiftKey &&
											(sendEnabled || onBlockedSubmit)
										) {
											e.preventDefault()
											handleSubmitAttempt()
										}
									}}
									onHeightChange={(height, meta) => {
										const rows = Math.round(height / meta.rowHeight)
										if (rows > 1) {
											setIsMultiLine(true)
										}
										setIsAtMaxRows(rows >= 7)
									}}
									minRows={1}
									maxRows={7}
								/>
							)}
						</div>

						<div
							className={cn(
								"flex flex-row items-center gap-1",
								leadingActions
									? "w-full justify-between"
									: cn("shrink-0", isMultiLine && "justify-end"),
							)}
						>
							{leadingActions ? (
								<div className="flex min-w-0 items-center gap-1">
									{leadingActions}
								</div>
							) : null}
							<div
								inert={!actionsVisible}
								className="flex shrink-0 flex-row items-center gap-1"
							>
								{onFilesAdded && (
									<Button
										data-testid="chat-input-attach"
										onClick={() =>
											document.getElementById(`${inputId}-file-upload`)?.click()
										}
										variant="ghost"
										Icon={Paperclip}
										iconClassName={chatInputAttachIconClasses()}
										iconStrokeWidth={CHAT_INPUT_ATTACH_STROKE_WIDTH}
										className={cn(
											"rounded-lg p-0 text-secondary hover:text-primary dark:text-secondary dark:hover:text-primary",
											compact ? "h-8 w-8" : "h-[30px] w-[30px]",
											// The paperclip and mic read as one pair, apart from send.
											showRecordButton && "-mr-1",
											revealClasses(2),
											!attachOnMount && lateControlClasses,
										)}
										disabled={attachmentsDisabled}
									/>
								)}
								{showRecordButton && (
									<Button
										type="button"
										aria-label="Dictate"
										variant="ghost"
										disabled={disabled}
										onClick={() => {
											if (recordingNeedsSecureOrigin) {
												handleVoiceError("insecure-origin")
												return
											}
											setVoiceError(null)
											void audio.start()
										}}
										wrapperClassName="contents"
										className={cn(
											"rounded-lg p-0",
											compact ? "h-8 w-8" : "h-[30px] w-[30px]",
											revealClasses(1),
											!voiceButtonOnMount && lateControlClasses,
										)}
									>
										<Microphone
											className={cn(
												"h-4 w-4 flex-none",
												CHAT_INPUT_IDLE_ICON_COLOR,
											)}
										/>
									</Button>
								)}
								{showVoiceButton && handleTranscript && !transcribeAudio && (
									<VoiceInputButton
										speech={speech}
										disabled={disabled}
										className={cn(compact && "h-8 w-8", revealClasses(1))}
									/>
								)}

								{isGenerating && onStop ? (
									<Button
										onClick={onStop}
										variant="ghost"
										wrapperClassName="contents"
										className={cn(
											"min-h-0 rounded-full bg-gray-900 p-0 transition-all hover:bg-gray-850 hover:text-white dark:bg-gray-50 dark:hover:bg-gray-200",
											compact ? "h-8 w-8" : "h-[26px] w-[26px]",
										)}
									>
										<span aria-hidden className={chatInputStopIconClasses()} />
									</Button>
								) : (
									<Button
										disabled={!sendEnabled && !onBlockedSubmit}
										onClick={handleSubmitAttempt}
										variant="ghost"
										wrapperClassName="contents"
										className={cn(
											"min-h-0 rounded-full p-0 disabled:opacity-100",
											compact ? "h-8 w-8" : "h-[26px] w-[26px]",
											sendEnabled
												? "bg-gray-900 hover:bg-gray-850 hover:text-white dark:bg-gray-50 dark:hover:bg-gray-200"
												: (idleSubmitClassName ??
														"border border-black/15 bg-white hover:bg-gray-100 dark:border-white/15 dark:bg-gray-700 dark:hover:bg-gray-600"),
											revealClasses(0),
										)}
									>
										<SubmitIcon
											className={chatInputSubmitIconClasses({
												submitIcon,
												sendEnabled,
											})}
										/>
									</Button>
								)}
							</div>
						</div>
					</div>
				</div>
			</motion.div>
			{recordingActive && (
				<VoiceRecordingBar
					state={audio.state}
					levels={audio.levels}
					compact={compact}
					onCancel={audio.cancel}
					onStop={audio.stop}
				/>
			)}
		</div>
	)
}
